import * as React from "react";
import { Route, Routes } from "react-router-dom";
import { FullPageLoader } from "./components";
import { HomePage } from "./pages/home";
import { UnauthorizedPage } from "./pages/UnauthorizedPage";
import { Navigate } from "react-router";
import { ActionCode } from "./api";
import { useCommonHooks } from "./hooks";

export enum ERoutes {
  home = "/home",
  translations = "/translations",
  notFound = "/404",
  unauthorized = "/401",
  users = "/users",
  sites = "/sites",
  rooms = "/rooms",
  trainingTypes = "/trainingtypes",
  employees = "/employees",
  trainings = "/training",
  protimePlanning = "/protimePlanning",
  newComers = "/newComers",
  dataTranslations = "/dataTranslations",
  smsTemplates = "/smstemplates",
  userRoles = "/userRoles",
  attendance = "/attendance",
  employeePlanning = "/employeePlanning",
  roomPlanning = "/roomPlanning",
  meeting = "/meeting",
  partnerTypes = "/partnerTypes",
  assignments = "/assignments",
  meetingTypes = "/meetingTypes",
  hourlyTypes = "/hourlyTypes",
  partners = "/partners",
  functions = "/functions",
  dashboard = "/dashboard",
  duties = "/duties",
  thematicTypes = "/thematicTypes",
}

const getEditRoute = (base: ERoutes) => {
  return "" + base + "/:id/:tab/:state?";
};

export const AppRouter = () => {
  const { user, hasAction } = useCommonHooks();
  return (
    <React.Suspense fallback={<FullPageLoader />}>
      <Routes>
        {user.newComerId ? (
          <>
            <Route path={`${ERoutes.home}`} Component={newComerItemPage} />
            <Route path={`${ERoutes.newComers}`} Component={newComerItemPage} />
            <Route
              path={`${ERoutes.unauthorized}`}
              Component={UnauthorizedPage}
            />
            <Route path="*" element={<Navigate to={ERoutes.newComers} />} />
          </>
        ) : (
          <>
            <Route path={`${ERoutes.home}`} Component={HomePage} />
            <Route
              path={`${ERoutes.translations}`}
              Component={translationEditPage}
              action={hasAction([ActionCode.EditTranslations])}
            />
            <Route
              path={`${ERoutes.users}`}
              Component={userListPage}
              action={hasAction([ActionCode.ViewUsers])}
            />
            <Route
              path={`${ERoutes.sites}`}
              Component={siteListPage}
              action={hasAction([ActionCode.ViewSites])}
            />
            <Route
              path={getEditRoute(ERoutes.sites)}
              Component={siteItemPage}
              action={hasAction([ActionCode.ViewSites])}
            />
            <Route
              path={getEditRoute(ERoutes.users)}
              Component={userItemPage}
              action={hasAction([ActionCode.ViewUsers])}
            />
            <Route
              path={`${ERoutes.trainingTypes}`}
              Component={trainingTypeListPage}
              action={hasAction([ActionCode.ViewTrainingTypes])}
            />
            <Route
              path={getEditRoute(ERoutes.trainingTypes)}
              Component={trainingTypeItemPage}
              action={hasAction([ActionCode.ViewTrainingTypes])}
            />
            <Route
              path={`${ERoutes.rooms}`}
              Component={roomListPage}
              action={hasAction([ActionCode.ViewRooms])}
            />
            <Route
              path={getEditRoute(ERoutes.rooms)}
              Component={roomItemPage}
              action={hasAction([ActionCode.ViewRooms])}
            />
            <Route
              path={`${ERoutes.employees}`}
              Component={employeeListPage}
              action={hasAction([ActionCode.ViewEmployees])}
            />
            <Route
              path={getEditRoute(ERoutes.employees)}
              Component={employeeItemPage}
              action={hasAction([ActionCode.ViewEmployees])}
            />
            <Route
              path={`${ERoutes.trainings}`}
              Component={trainingListPage}
              action={hasAction([ActionCode.ViewTrainings])}
            />
            <Route
              path={`${getEditRoute(ERoutes.trainings)}/:sessionId?`}
              Component={trainingItemPage}
              action={hasAction([ActionCode.ViewTrainings])}
            />
            <Route
              path={`${ERoutes.protimePlanning}`}
              Component={protimePlanning}
              action={hasAction([ActionCode.ViewTrainings])}
            />
            <Route
              path={`${ERoutes.employeePlanning}`}
              Component={employeePlanning}
              action={hasAction([ActionCode.ViewTrainings])}
            />
            <Route
              path={`${ERoutes.roomPlanning}`}
              Component={roomPlanning}
              action={hasAction([ActionCode.ViewRoomPlanning])}
            />
            <Route
              path={`${ERoutes.smsTemplates}`}
              Component={smsTemplateListPage}
              action={hasAction([ActionCode.ViewSmsTemplates])}
            />
            <Route
              path={getEditRoute(ERoutes.smsTemplates)}
              Component={smsTemplateItemPage}
              action={hasAction([ActionCode.ViewSmsTemplates])}
            />
            <Route
              path={`${ERoutes.newComers}`}
              Component={newcomerListPage}
              action={hasAction([ActionCode.ViewNewComers])}
            />
            <Route
              path={`${getEditRoute(ERoutes.newComers)}`}
              Component={newComerItemPage}
              action={hasAction([ActionCode.ViewNewComers])}
            />
            <Route
              path={`${ERoutes.dataTranslations}`}
              Component={dataTranslationEditPage}
              action={hasAction([ActionCode.EditDataTranslations])}
            />
            <Route
              path={`${ERoutes.userRoles}/:tab?`}
              Component={userRoleTab}
              action={hasAction([ActionCode.ViewRoles])}
            />
            <Route
              path={`${ERoutes.attendance}`}
              Component={attendanceListPage}
              action={hasAction([ActionCode.ViewAttendance])}
            />
            <Route
              path={`${getEditRoute(ERoutes.meeting)}`}
              Component={appointmentItemPage}
              action={hasAction([ActionCode.ViewMeeting])}
            />
            <Route
              path={`${ERoutes.meeting}`}
              Component={appointmentListPage}
              action={hasAction([ActionCode.ViewMeeting])}
            />
            <Route
              path={`${ERoutes.meetingTypes}`}
              Component={meetingTypeListPage}
              action={hasAction([ActionCode.ViewMeetingType])}
            />
            <Route
              path={getEditRoute(ERoutes.meetingTypes)}
              Component={meetingTypeItemPage}
              action={hasAction([ActionCode.ViewMeetingType])}
            />
            <Route
              path={`${ERoutes.hourlyTypes}`}
              Component={hourlyTypeListPage}
              action={hasAction([ActionCode.ViewHourlyType])}
            />
            <Route
              path={getEditRoute(ERoutes.hourlyTypes)}
              Component={hourlyTypeItemPage}
              action={hasAction([ActionCode.ViewHourlyType])}
            />
            <Route
              path={`${ERoutes.assignments}`}
              Component={assignmentListPage}
              action={hasAction([ActionCode.ViewAssignment])}
            />
            <Route
              path={getEditRoute(ERoutes.assignments)}
              Component={assignmentItemPage}
              action={hasAction([ActionCode.ViewAssignment])}
            />
            <Route
              path={`${ERoutes.functions}`}
              Component={functionListPage}
              action={hasAction([ActionCode.ViewFunction])}
            />
            <Route
              path={getEditRoute(ERoutes.functions)}
              Component={functionItemPage}
              action={hasAction([ActionCode.ViewFunction])}
            />
            <Route
              path={`${ERoutes.partnerTypes}`}
              Component={partnerTypeListPage}
              action={hasAction([ActionCode.ViewPartnerTypes])}
            />
            <Route
              path={getEditRoute(ERoutes.partnerTypes)}
              Component={partnerTypeItemPage}
              action={hasAction([ActionCode.ViewPartnerTypes])}
            />
            <Route
              path={`${ERoutes.thematicTypes}`}
              Component={thematicTypeListPage}
              action={hasAction([ActionCode.ViewThematicTypes])}
            />
            <Route
              path={getEditRoute(ERoutes.thematicTypes)}
              Component={thematicTypeItemPage}
              action={hasAction([ActionCode.ViewThematicTypes])}
            />
            <Route
              path={`${ERoutes.partners}`}
              Component={partnerListPage}
              action={hasAction([ActionCode.ViewPartners])}
            />
            <Route
              path={getEditRoute(ERoutes.partners)}
              Component={partnerItemPage}
              action={hasAction([ActionCode.ViewPartners])}
            />
            <Route
              path={`${ERoutes.dashboard}`}
              Component={dashboardListPage}
              action={hasAction([ActionCode.ViewMeetingDashboard])}
            />
            {/* <Route
              path={`${ERoutes.reminderPhoneCalls}/:tab?`}
              Component={reminderPhoneCallPage}
              action={hasAction([ActionCode.ViewReminderPhoneCalls])}
            /> */}
            <Route
              path={`${ERoutes.duties}`}
              Component={dutyListPage}
              action={hasAction([ActionCode.ViewDuties])}
            />
            <Route
              path={getEditRoute(ERoutes.duties)}
              Component={dutyItemPage}
              action={hasAction([ActionCode.ViewDuties])}
            />
            <Route
              path={`${ERoutes.unauthorized}`}
              Component={UnauthorizedPage}
            />
            <Route
              path="*"
              element={<Navigate to={ERoutes.protimePlanning} />}
            />
          </>
        )}
      </Routes>
    </React.Suspense>
  );
};

const userListPage = React.lazy(
  () => import("./pages/users/list/UserListPage")
);

const translationEditPage = React.lazy(
  () => import("./pages/translation/TranslationEditPage")
);

const siteListPage = React.lazy(
  () => import("./pages/sites/list/SiteListPage")
);

const siteItemPage = React.lazy(
  () => import("./pages/sites/item/SiteItemPage")
);

const userItemPage = React.lazy(
  () => import("./pages/users/item/userItemPage")
);

const trainingTypeListPage = React.lazy(
  () => import("./pages/trainingTypes/list/TrainingTypeListPage")
);

const trainingTypeItemPage = React.lazy(
  () => import("./pages/trainingTypes/item/TrainingTypeItemPage")
);

const roomListPage = React.lazy(
  () => import("./pages/rooms/list/RoomListPage")
);

const roomItemPage = React.lazy(
  () => import("./pages/rooms/item/RoomItemPage")
);

const employeeListPage = React.lazy(
  () => import("./pages/employees/list/EmployeeListPage")
);

const employeeItemPage = React.lazy(
  () => import("./pages/employees/item/EmployeeItemPage")
);

const trainingListPage = React.lazy(
  () => import("./pages/trainings/list/TrainingListPage")
);

const trainingItemPage = React.lazy(
  () => import("./pages/trainings/item/TrainingItemPage")
);

const protimePlanning = React.lazy(
  () => import("./pages/planning/protime/ProtimePlanning")
);
const employeePlanning = React.lazy(
  () => import("./pages/planning/employee/EmployeePlanning")
);
const roomPlanning = React.lazy(
  () => import("./pages/planning/room/RoomPlanning")
);

const smsTemplateListPage = React.lazy(
  () => import("./pages/smsTemplates/list/SmsTemplateListPage")
);

const smsTemplateItemPage = React.lazy(
  () => import("./pages/smsTemplates/items/SmsTemplateItemPage")
);

const newcomerListPage = React.lazy(
  () => import("./pages/newComers/list/NewComerListPage")
);

const newComerItemPage = React.lazy(
  () => import("./pages/newComers/item/NewComerItemPage")
);

const dataTranslationEditPage = React.lazy(
  () => import("./pages/translation/DataTranslationEditPage")
);

const userRoleTab = React.lazy(
  () => import("./pages/userRoles/UserRoleListPage")
);

const attendanceListPage = React.lazy(
  () => import("./pages/attendance/AttendanceListPage")
);

const appointmentItemPage = React.lazy(
  () => import("./pages/meeting/item/MeetingItemPage")
);
const appointmentListPage = React.lazy(
  () => import("./pages/meeting/list/MeetingListPage")
);
const partnerTypeListPage = React.lazy(
  () => import("./pages/partnerTypes/list/PartnerTypeListPage")
);

const partnerTypeItemPage = React.lazy(
  () => import("./pages/partnerTypes/item/PartnerTypeItemPage")
);

const thematicTypeListPage = React.lazy(
  () => import("./pages/thematicTypes/list/ThematicTypeListPage")
);

const thematicTypeItemPage = React.lazy(
  () => import("./pages/thematicTypes/item/ThematicTypeItemPage")
);

const meetingTypeListPage = React.lazy(
  () => import("./pages/meetingTypes/list/MeetingTypeListPage")
);

const meetingTypeItemPage = React.lazy(
  () => import("./pages/meetingTypes/item/MeetingTypeItemPage")
);

const hourlyTypeListPage = React.lazy(
  () => import("./pages/hourlyTypes/list/HourlyTypeListePage")
);

const hourlyTypeItemPage = React.lazy(
  () => import("./pages/hourlyTypes/item/HourlyTypeItemPage")
);
const assignmentListPage = React.lazy(
  () => import("./pages/assignments/list/AssignmentListPage")
);

const assignmentItemPage = React.lazy(
  () => import("./pages/assignments/item/AssignmentItemPage")
);
const functionListPage = React.lazy(
  () => import("./pages/functions/list/FunctionListPage")
);

const functionItemPage = React.lazy(
  () => import("./pages/functions/item/FunctionItemPage")
);
const partnerListPage = React.lazy(
  () => import("./pages/partners/list/PartnerListPage")
);

const partnerItemPage = React.lazy(
  () => import("./pages/partners/item/PartnerItemPage")
);

const dashboardListPage = React.lazy(
  () => import("./pages/dashboard/DashboardListPage")
);
// const reminderPhoneCallPage = React.lazy(
//   () => import("./pages/reminderPhoneCalls/ReminderPhoneCallPage")
// );
const dutyListPage = React.lazy(
  () => import("./pages/duties/list/DutyListPage")
);

const dutyItemPage = React.lazy(
  () => import("./pages/duties/item/DutyItemPage")
);
